import React, { useEffect, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { paymentType, initPaymentType } from '../../../model/master';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api_payment_type } from '../../../api/sale';
import CommonTableList from '../../common/common-table-list';
import CommonDetail from '../../common/common-detail';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Alert, Modal, Tag } from 'antd';
import { isEmpty } from '../../../utils/util';
import { isConfirmedWrite } from '../../../utils/write-result';
import PaymentTypeDetail from './payment-type-detail';
import { printProfileCard } from '../../../utils/record-print-utils';
import { deleteFailureMessage } from '../../../utils/write-result';

/*
 * Phase 7C-6C sweep: `SEED_PAYMENT_TYPES` removed -- five invented payment
 * methods with invented creation dates, used as the initial state AND as
 * the fallback whenever the API returned an empty list or failed. A shop
 * that had configured its own methods saw these instead, and one that had
 * configured none was told it had five. The list starts empty and stays
 * empty when Backend cannot answer.
 */

const getPaymentIcon = (code: string, name: string) => {
    const text = `${code} ${name}`.toLowerCase();
    if (text.includes('เงินสด') || text.includes('cash') || code === 'C') return '💵';
    if (text.includes('qr') || text.includes('promptpay') || text.includes('สแกน')) return '📱';
    if (text.includes('บัตร') || text.includes('credit') || text.includes('card') || code === 'CC') return '💳';
    if (text.includes('คูปอง') || text.includes('coupon') || code === 'CP') return '🎟️';
    if (text.includes('โอน') || text.includes('transfer') || code === 'T') return '🏦';
    return '💰';
};

const PaymentType: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('');
    const [title, setTitle] = useState('');
    const [data, setData] = useState<paymentType[]>([]);
    const [dataState, setDataState] = useState<'loading' | 'ready' | 'error'>('loading');
    const [formData, setFormData] = useState<paymentType>(initPaymentType);

    const filterField: any = ['code', 'name', 'remark'];
    const placeholder_txt: string = 'ค้นหาตามรหัส, ชื่อประเภทการชำระเงิน, หรือหมายเหตุ...';

    const onPrintPaymentType = (record: any) => {
        printProfileCard('ข้อมูลประเภทการชำระเงิน (PAYMENT METHOD)', [
            { label: 'รหัสประเภท', value: record.code },
            { label: 'ชื่อประเภทการชำระเงิน', value: record.name },
            { label: 'คำอธิบาย / รายละเอียด', value: record.remark || '-' },
            { label: 'สถานะ', value: record.status === 1 ? '🟢 ใช้งานอยู่' : '⚪ ปิดใช้งาน' }
        ]);
    };

    const totalActive = data.filter(x => x.status === 1 || x.status === undefined || x.status === null).length;

    const columns: ColumnsType<paymentType> = [
        {
            title: 'ลำดับ',
            dataIndex: 'key',
            key: 'key',
            width: '70px',
            align: 'center',
            render: (_text, _record, index) => index + 1
        },
        {
            title: 'รหัส (Code)',
            dataIndex: 'code',
            key: 'code',
            width: '120px',
            render: (code) => (
                <Tag color="orange" style={{ fontWeight: 700, fontSize: '13px', padding: '2px 10px', borderRadius: '6px' }}>
                    {code || '-'}
                </Tag>
            )
        },
        { 
            title: 'ชื่อประเภทการชำระเงิน', 
            dataIndex: 'name', 
            key: 'name',
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600 }}>
                    <span style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '10px',
                        background: 'var(--primary-light, #FFF3EE)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '17px'
                    }}>
                        {getPaymentIcon(record.code, text)}
                    </span>
                    <span style={{ fontSize: '14px', color: 'inherit' }}>{text}</span>
                </div>
            )
        },
        { 
            title: 'คำอธิบาย / หมายเหตุ', 
            dataIndex: 'remark', 
            key: 'remark',
            render: (text) => text ? <span style={{ color: 'var(--text-secondary, #64748B)' }}>{text}</span> : <span style={{ color: '#94A3B8' }}>-</span>
        },
        { 
            title: 'สถานะ', 
            dataIndex: 'status', 
            key: 'status',
            width: '130px',
            align: 'center',
            render: (status) => {
                const isActive = status === 1 || status === undefined || status === null;
                return (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        background: isActive ? 'rgba(16, 185, 129, 0.12)' : 'rgba(148, 163, 184, 0.15)',
                        color: isActive ? '#10B981' : '#64748B'
                    }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isActive ? '#10B981' : '#94A3B8' }}></span>
                        {isActive ? 'ใช้งานปกติ' : 'ปิดการใช้งาน'}
                    </span>
                );
            }
        },
        { 
            title: 'การดำเนินการ', 
            dataIndex: 'operation', 
            key: 'operation',
            width: '160px', 
            fixed: 'right', 
            render: (_, record) => (
                <div className='iconOperation'>
                    <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => onShowDetail('VIEW', record)} />
                    <FontAwesomeIcon icon="print" title="พิมพ์ประเภทการชำระเงิน" onClick={() => onPrintPaymentType(record)} />
                    <FontAwesomeIcon icon="pen-to-square" title="แก้ไขข้อมูล" onClick={() => onShowDetail('EDIT', record)} />
                    <FontAwesomeIcon icon="trash-can" title="ลบข้อมูล" onClick={() => onDelete(record.id)} />
                </div>
            )
        }
    ];

    const init = async () => {
        try {
            const res = await api_payment_type('LIST', {});
            // Always replace, even with an empty list: a shop that has
            // configured no payment methods has none, and must not be
            // shown a set it never created.
            setData(Array.isArray(res) ? res.map((item: any, idx: number) => ({ ...item, key: idx + 1 })) : []);
            setDataState('ready');
        } catch (e) {
            setData([]);
            setDataState('error');
            console.error(e);
        }
    };

    const getConfigModal = (modalTitle: string, content: string) => {
        return {
            title: modalTitle,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        };
    };

    const onShowDetail = (showMode: string, recordData?: paymentType) => {
        if (showMode === 'ADD') {
            setTitle('เพิ่มประเภทการชำระเงิน');
            setFormData({ ...initPaymentType });
        } else if (showMode === 'COPY') {
            setTitle('เพิ่มประเภทการชำระเงิน (คัดลอก)');
            setFormData(recordData ? { ...recordData, id: 0, code: `${recordData.code}_COPY`, name: `${recordData.name} (คัดลอก)` } : { ...initPaymentType });
        } else if (showMode === 'EDIT') {
            setTitle('แก้ไขประเภทการชำระเงิน');
            setFormData(recordData ? { ...recordData } : { ...initPaymentType });
        } else if (showMode === 'VIEW') {
            setTitle('รายละเอียดประเภทการชำระเงิน');
            setFormData(recordData ? { ...recordData } : { ...initPaymentType });
        }

        setMode(showMode);
        setOpen(true);
    };

    const onCloseDetail = () => {
        setOpen(false);
    };

    const onSave = async () => {
        let operation = 'INSERT';
        let key_id = undefined;
        const body = { ...formData };

        if (body.id && body.id > 0 && mode === 'EDIT') {
            operation = 'UPDATE';
            key_id = body.id;
        }

        if (!isEmpty(body.name) && !isEmpty(body.code)) {
            body.code = body.code.toUpperCase().trim();
            body.name = body.name.trim();
            body.status = body.status !== undefined ? body.status : 1;

            try {
                const res = await api_payment_type(operation, body, key_id);
                // Same defect as Purchase Order: `|| res` accepted the []
                // this wrapper returns on failure, because [] is truthy.
                if (isConfirmedWrite(res)) {
                    setOpen(false);
                    Modal.success({
                        ...getConfigModal('สำเร็จ', operation === 'UPDATE' ? 'แก้ไขประเภทการชำระเงินเรียบร้อยแล้ว' : 'เพิ่มประเภทการชำระเงินเรียบร้อยแล้ว'),
                        onOk() { init(); }
                    });
                } else {
                    showSaveFailed(operation);
                }
            } catch (e) {
                // The auth interceptor has already dealt with a 401.
                showSaveFailed(operation);
            }
        } else {
            Modal.error({
                ...getConfigModal('ข้อมูลไม่ครบถ้วน', 'กรุณากรอกรหัสประเภทและชื่อประเภทการชำระเงินให้ครบถ้วน')
            });
        }
    };

    /**
     * Replaces `updateLocalData`, which on a failed write invented an id,
     * pushed the row into the table as though it had been saved, closed
     * the drawer and announced success. The drawer stays open now with
     * what was typed, so it can be retried.
     */
    const showSaveFailed = (operation: string) => {
        Modal.error({
            ...getConfigModal(
                'บันทึกไม่สำเร็จ',
                operation === 'UPDATE'
                    ? 'ไม่สามารถแก้ไขประเภทการชำระเงินได้ ข้อมูลยังไม่ถูกบันทึก กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง'
                    : 'ไม่สามารถเพิ่มประเภทการชำระเงินได้ ข้อมูลยังไม่ถูกบันทึก กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง',
            )
        });
    };

    const onDelete = async (key_id: number) => {
        Modal.confirm({
            ...getConfigModal('ยืนยันการลบ', 'คุณต้องการลบประเภทการชำระเงินนี้หรือไม่ ? การดำเนินการนี้ไม่สามารถย้อนกลับได้'),
            async onOk() {
                // The result was ignored and the row removed either way, so
                // a failed delete still reported success and the row came
                // back on the next refresh.
                const generic = 'ไม่สามารถลบประเภทการชำระเงินได้ และข้อมูลยังคงอยู่ตามเดิม กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง';
                let confirmed = false;
                let failure: any = null;
                try {
                    confirmed = isConfirmedWrite(await api_payment_type('DELETE', {}, key_id));
                } catch (e) {
                    // The error was caught and thrown away, so a record that
                    // was already deleted read exactly like a connection
                    // problem. Keep it: Backend's 404 says which it was.
                    confirmed = false;
                    failure = e;
                }

                if (!confirmed) {
                    Modal.error({
                        ...getConfigModal('ลบไม่สำเร็จ', deleteFailureMessage(failure, generic))
                    });
                    return;
                }

                setData(prev => prev.filter(item => item.id !== key_id));
                Modal.success({
                    ...getConfigModal('สำเร็จ', 'ลบประเภทการชำระเงินเรียบร้อยแล้ว')
                });
            }
        });
    };

    const onFormChange = (updatedData: paymentType) => {
        setFormData(updatedData);
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <div data-testid="payment-type-page">
            {dataState === 'error' && (
                <Alert
                    type="error"
                    showIcon
                    data-testid="payment-type-load-error"
                    style={{ marginBottom: '12px', borderRadius: '8px' }}
                    message="โหลดข้อมูลไม่สำเร็จ"
                    description="ไม่สามารถติดต่อระบบได้ ตารางด้านล่างจึงยังว่างอยู่ ซึ่งไม่ได้แปลว่าไม่มีข้อมูล กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง"
                />
            )}

            <CommonTableList
                page='payment_type'
                placeholder={placeholder_txt}
                columns={columns}
                data={data}
                filterField={filterField}
                onAdd={() => onShowDetail('ADD')}
            />

            <CommonDetail
                width='480px'
                open={open}
                mode={mode}
                title={title}
                onClosed={onCloseDetail}
                onSaved={onSave}
                element={
                    <PaymentTypeDetail
                        paymentType={{ ...formData }}
                        disabled={mode === 'VIEW'}
                        onChange={onFormChange}
                    />
                }
            />
        </div>
    );
};

export default PaymentType;
